import StopIcon from '@mui/icons-material/Stop';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ArrowRight, LogoGray } from '@vy/ui-design-system';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  chatFooter,
  chatFooterDisclaimer,
  chatFooterPoweredBy,
  chatFooterPoweredByText,
  chatInputActionsRow,
  chatInputFieldContainer,
  chatInputRoot,
  chatInputSendButton,
  chatInputSendButtonEnabled,
  chatInputStopButton,
  chatInputTextarea,
} from '../styles';

const MAX_HEIGHT = 120;

type ChatInputProps = {
  onSend: (text: string) => void;
  onStop: () => void;
  isLoading: boolean;
};

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, onStop, isLoading }) => {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, [input]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isLoading) {
        onStop();
      } else {
        handleSend();
      }
    }
  };

  const hasInput = input.trim().length > 0;

  return (
    <Box sx={chatInputRoot}>
      <Box sx={chatInputFieldContainer}>
        <Box
          ref={textareaRef}
          component="textarea"
          data-vy="ai-copilot-chat-input"
          value={input}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your revenue, invoices, customers, or usage…"
          sx={chatInputTextarea}
        />
        <Box sx={chatInputActionsRow}>
          <Box />
          {isLoading ? (
            <Box component="button" data-vy="ai-copilot-stop" onClick={onStop} sx={chatInputStopButton}>
              <StopIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
          ) : (
            <Box
              component="button"
              data-vy="ai-copilot-send"
              onClick={handleSend}
              disabled={!hasInput}
              sx={hasInput ? chatInputSendButtonEnabled : chatInputSendButton}
            >
              <ArrowRight
                viewBox="0 0 34 34"
                width={28}
                height={28}
                style={{ transform: 'rotate(-90deg)', color: '#fff' }}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={chatFooter}>
        <Box sx={chatFooterPoweredBy}>
          <LogoGray width={16} height={16} />
          <Typography sx={chatFooterPoweredByText}>Powered by Vayu</Typography>
        </Box>
        <Typography sx={chatFooterDisclaimer}>
          AI-generated insights may be incorrect. Verify before acting.
        </Typography>
      </Box>
    </Box>
  );
};
